import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
import { useCachedAvatar } from "@/hooks/useCachedAvatar";
import { User, CreditCard, Settings, Link2, Shield, Plus, Check, Loader2, AlertTriangle, Bell, MoreVertical, ChevronDown, X, Beaker } from "lucide-react";

import quinnIconDark from "@/assets/quinn-icon.svg";
import quinnIconLight from "@/assets/quinn-icon-light.png";
import { BillingForm } from "@/components/BillingForm";
import { SubscriptionPlans } from "@/components/SubscriptionPlans";
import { MFASetupDialog } from "@/components/MFASetupDialog";
import { DeleteAccountDialog } from "@/components/DeleteAccountDialog";
import { useTheme } from "next-themes";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { useBetaFeatureAccess } from "@/hooks/useBetaFeatureAccess";

interface UserSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialSection?: SettingsSection;
}

type SettingsSection = "account" | "billing" | "preferences" | "notifications" | "quinn" | "connections" | "danger";

export function UserSettingsDialog({ open, onOpenChange, initialSection = "account" }: UserSettingsDialogProps) {
  const { user, resetPassword, getMFAFactors, unenrollMFA } = useAuth();
  const { avatarUrl } = useCachedAvatar();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const isMobile = useIsMobile();
  const [activeSection, setActiveSection] = useState<SettingsSection>(initialSection);
  const [isUploading, setIsUploading] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [isDisplayNameChanged, setIsDisplayNameChanged] = useState(false);
  const [isSavingName, setIsSavingName] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [mfaSetupOpen, setMfaSetupOpen] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaFactors, setMfaFactors] = useState<any[]>([]);
  const [isLoadingMFA, setIsLoadingMFA] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [sidebarHidden, setSidebarHidden] = useState(false);
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);
  const [unlinkProvider, setUnlinkProvider] = useState<'google' | 'discord' | 'apple' | null>(null);
  const [notifyAssignedTasks, setNotifyAssignedTasks] = useState(true);
  const [notifyFormSubmissions, setNotifyFormSubmissions] = useState(true);
  const [notifyMentions, setNotifyMentions] = useState(true);
  const [spotifyConnected, setSpotifyConnected] = useState(false);
  const [spotifyEmail, setSpotifyEmail] = useState<string | null>(null);
  const [isLoadingSpotify, setIsLoadingSpotify] = useState(false);
  const [showSpotifyUnlinkConfirm, setShowSpotifyUnlinkConfirm] = useState(false);
  const [quinnBetaEnabled, setQuinnBetaEnabled] = useState(false);
  const [showQuinnCreditUsage, setShowQuinnCreditUsage] = useState(false);
  const [isSavingQuinnBeta, setIsSavingQuinnBeta] = useState(false);
  const [isSavingQuinnCreditUsage, setIsSavingQuinnCreditUsage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Check if user has access to Quinn AI beta
  const { hasAccess: hasQuinnBetaAccess } = useBetaFeatureAccess('quinn-ai');

  const getUserDisplayName = () => {
    return displayName || 
           user?.email?.split('@')[0] || 
           "User";
  };

  // Set initial section when dialog opens
  useEffect(() => {
    if (open && initialSection) {
      setActiveSection(initialSection);
    }
  }, [open, initialSection]);
  
  // Load preferences including display name from profiles table
  useEffect(() => {
    const loadPreferences = async () => {
      if (!user || !open) return;
      
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('display_name, sidebar_hidden_by_default, notify_assigned_tasks, notify_form_submissions, notify_mentions')
          .eq('user_id', user.id)
          .single();
        
        if (!error && data) {
          if (data.display_name) {
            setDisplayName(data.display_name);
          }
          setSidebarHidden(data.sidebar_hidden_by_default || false);
          setNotifyAssignedTasks(data.notify_assigned_tasks ?? true);
          setNotifyFormSubmissions(data.notify_form_submissions ?? true);
          setNotifyMentions(data.notify_mentions ?? true);
        }
      } catch (error) {
        console.error('Error loading preferences:', error);
      }
    };

    const loadSpotifyConnection = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('spotify_integrations')
          .select('spotify_display_name')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (!error && data) {
          setSpotifyConnected(true);
          setSpotifyEmail(data.spotify_display_name);
        } else {
          setSpotifyConnected(false);
          setSpotifyEmail(null);
        }
      } catch (error) {
        console.error('Error loading Spotify connection:', error);
      }
    };

    const loadQuinnBetaPreference = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('user_app_preferences')
          .select('preferences')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (!error && data?.preferences) {
          const prefs = data.preferences as { quinn_beta_enabled?: boolean; show_quinn_credit_usage?: boolean };
          setQuinnBetaEnabled(prefs.quinn_beta_enabled ?? false);
          setShowQuinnCreditUsage(prefs.show_quinn_credit_usage ?? false);
        }
      } catch (error) {
        console.error('Error loading Quinn beta preference:', error);
      }
    };
    
    loadPreferences();
    loadSpotifyConnection();
    loadQuinnBetaPreference();
  }, [user, open]);

  const handleToggleQuinnBeta = async (enabled: boolean) => {
    if (!user) return;
    
    setIsSavingQuinnBeta(true);
    try {
      // Get current preferences
      const { data: currentData } = await supabase
        .from('user_app_preferences')
        .select('preferences, app_order')
        .eq('user_id', user.id)
        .maybeSingle();
      
      const currentPrefs = (currentData?.preferences as Record<string, unknown>) || {};
      
      const { error } = await supabase
        .from('user_app_preferences')
        .upsert({
          user_id: user.id,
          app_order: currentData?.app_order || [],
          preferences: { ...currentPrefs, quinn_beta_enabled: enabled }
        }, { onConflict: 'user_id' });
      
      if (error) throw error;
      
      setQuinnBetaEnabled(enabled);
      toast.success(enabled ? 'Quinn AI beta features enabled' : 'Quinn AI beta features disabled');
    } catch (error) {
      console.error('Error saving Quinn beta preference:', error);
      toast.error('Failed to save preference');
    } finally {
      setIsSavingQuinnBeta(false);
    }
  };

  const handleToggleQuinnCreditUsage = async (enabled: boolean) => {
    if (!user) return;
    
    setIsSavingQuinnCreditUsage(true);
    try {
      // Get current preferences
      const { data: currentData } = await supabase
        .from('user_app_preferences')
        .select('preferences, app_order')
        .eq('user_id', user.id)
        .maybeSingle();
      
      const currentPrefs = (currentData?.preferences as Record<string, unknown>) || {};
      
      const { error } = await supabase
        .from('user_app_preferences')
        .upsert({
          user_id: user.id,
          app_order: currentData?.app_order || [],
          preferences: { ...currentPrefs, show_quinn_credit_usage: enabled }
        }, { onConflict: 'user_id' });
      
      if (error) throw error;
      
      setShowQuinnCreditUsage(enabled);
      toast.success(enabled ? 'Credit usage display enabled' : 'Credit usage display disabled');
    } catch (error) {
      console.error('Error saving credit usage preference:', error);
      toast.error('Failed to save preference');
    } finally {
      setIsSavingQuinnCreditUsage(false);
    }
  };

  const updateNotificationPreference = async (column: string, value: boolean) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ [column]: value })
        .eq('user_id', user.id);
      
      if (error) throw error;
      toast.success('Notification preference updated');
    } catch (error) {
      console.error('Error updating notification preference:', error);
      toast.error('Failed to update notification preference');
    }
  };

  const getUserInitials = () => {
    const name = getUserDisplayName();
    return name.slice(0, 2).toUpperCase();
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    setIsUploading(true);
    try {
      // Delete old avatar if exists
      if (avatarUrl) {
        const oldPath = avatarUrl.split('/').pop();
        if (oldPath) {
          await supabase.storage.from('avatars').remove([`${user.id}/${oldPath}`]);
        }
      }

      // Upload new avatar
      const fileExt = file.name.split('.').pop();
      const filePath = `${user.id}/${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // Update profile
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      toast.success("Profile picture updated successfully");
      window.location.reload(); // Refresh to show new avatar
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error("Failed to upload profile picture");
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!user || !avatarUrl) return;

    setIsUploading(true);
    try {
      // Delete from storage
      const oldPath = avatarUrl.split('/').pop();
      if (oldPath) {
        await supabase.storage.from('avatars').remove([`${user.id}/${oldPath}`]);
      }

      // Update profile to null
      const { error } = await supabase
        .from('profiles')
        .update({ avatar_url: null })
        .eq('user_id', user.id);

      if (error) throw error;

      toast.success("Profile picture removed successfully");
      window.location.reload(); // Refresh to show changes
    } catch (error) {
      console.error('Error removing avatar:', error);
      toast.error("Failed to remove profile picture");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDisplayNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setDisplayName(newValue);
    setIsDisplayNameChanged(newValue !== getUserDisplayName());
  };

  const handleSaveDisplayName = async () => {
    if (!user || !displayName.trim()) return;

    setIsSavingName(true);
    try {
      // Update auth metadata which will trigger the sync to profiles table
      const { error: authError } = await supabase.auth.updateUser({
        data: { display_name: displayName.trim() }
      });

      if (authError) throw authError;

      // Also update profiles table directly
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ display_name: displayName.trim() })
        .eq('user_id', user.id);

      if (profileError) throw profileError;

      toast.success("Display name updated successfully");
      setIsDisplayNameChanged(false);
      
      // Reload the session to get updated user data
      const { data: { session } } = await supabase.auth.refreshSession();
      if (session) {
        // Force re-render by closing and reopening dialog
        setTimeout(() => window.location.reload(), 500);
      }
    } catch (error) {
      console.error('Error updating display name:', error);
      toast.error("Failed to update display name");
    } finally {
      setIsSavingName(false);
    }
  };

  const handleChangeEmail = async () => {
    if (!newEmail.trim() || !user) return;

    setIsChangingEmail(true);
    try {
      const { error } = await supabase.auth.updateUser({
        email: newEmail.trim(),
      });

      if (error) throw error;

      toast.success("Confirmation email sent! Check your inbox to verify the new email.");
      setNewEmail("");
      setShowEmailChange(false);
    } catch (error: any) {
      console.error('Error changing email:', error);
      toast.error(error.message || "Failed to change email");
    } finally {
      setIsChangingEmail(false);
    }
  };

  const handleResetPassword = async () => {
    if (!user?.email) return;

    setIsChangingPassword(true);
    try {
      const { error } = await resetPassword(user.email);

      if (error) throw error;

      toast.success("Password reset link sent to your email!");
    } catch (error: any) {
      console.error('Error sending password reset:', error);
      toast.error(error.message || "Failed to send password reset email");
    } finally {
      setIsChangingPassword(false);
    }
  };

  const loadMFAStatus = async () => {
    setIsLoadingMFA(true);
    try {
      const { error, factors } = await getMFAFactors();
      if (error) throw error;
      
      setMfaFactors(factors || []);
      setMfaEnabled((factors || []).length > 0);
    } catch (error: any) {
      console.error('Failed to load MFA status:', error);
    } finally {
      setIsLoadingMFA(false);
    }
  };

  const handleDisableMFA = async (factorId: string) => {
    setIsLoadingMFA(true);
    try {
      const { error } = await unenrollMFA(factorId);
      if (error) throw error;

      toast.success("2-Step Verification disabled");
      await loadMFAStatus();
    } catch (error: any) {
      console.error('Failed to disable MFA:', error);
      toast.error(error.message || "Failed to disable MFA");
    } finally {
      setIsLoadingMFA(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadMFAStatus();
    }
  }, [open]);

  const sidebarItems = [
    {
      id: "account" as SettingsSection,
      label: "My Account",
      icon: User,
    },
    {
      id: "preferences" as SettingsSection,
      label: "Preferences",
      icon: Settings,
    },
    {
      id: "notifications" as SettingsSection,
      label: "Notifications",
      icon: Bell,
    },
    {
      id: "quinn" as SettingsSection,
      label: "Quinn AI",
      icon: null,
      customIcon: resolvedTheme === 'dark' ? quinnIconLight : quinnIconDark,
      badge: "Beta",
    },
    {
      id: "connections" as SettingsSection,
      label: "Connections",
      icon: Link2,
    },
    {
      id: "billing" as SettingsSection,
      label: "Billing",
      icon: CreditCard,
    },
    {
      id: "danger" as SettingsSection,
      label: "Danger Zone",
      icon: AlertTriangle,
    },
  ];

  const renderContent = () => {
    switch (activeSection) {
      case "account":
        return (
          <div className="space-y-8 max-w-4xl">
            {/* Account Header */}
            <div>
              <h2 className="text-2xl font-semibold mb-1">Account</h2>
            </div>

            {/* Profile Section */}
            <div className="space-y-6">
              {/* Avatar and Name */}
              <div className="flex items-start gap-6 pb-6 border-b">
                <Avatar className="h-20 w-20">
                  <AvatarImage src={avatarUrl || undefined} alt={getUserDisplayName()} />
                  <AvatarFallback className="text-2xl">
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 space-y-4">
                  <div>
                    <label className="text-sm text-muted-foreground block mb-2">
                      Display name
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={displayName}
                        onChange={handleDisplayNameChange}
                        className="flex-1 max-w-md bg-muted/50 border-0 rounded-md px-4 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Enter your name"
                      />
                      {isDisplayNameChanged && (
                        <Button
                          onClick={handleSaveDisplayName}
                          disabled={isSavingName}
                          size="sm"
                        >
                          {isSavingName ? "Saving..." : "Save"}
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                    <button 
                      className="text-primary hover:underline disabled:opacity-50"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                    >
                      {isUploading ? "Uploading..." : "Add photo"}
                    </button>
                    <span className="text-muted-foreground">or</span>
                    <button 
                      className="text-primary hover:underline disabled:opacity-50"
                      onClick={handleRemovePhoto}
                      disabled={isUploading || !avatarUrl}
                    >
                      Remove photo
                    </button>
                  </div>
                </div>
              </div>

              {/* Account Security Header */}
              <div>
                <h3 className="text-xl font-semibold">Account security</h3>
              </div>

              {/* Email Section */}
              <div className="py-4 border-b">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="font-medium mb-1">Email</h4>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowEmailChange(!showEmailChange)}
                  >
                    Change email
                  </Button>
                </div>
                {showEmailChange && (
                  <div className="flex items-center gap-2 mt-3">
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      className="flex-1 bg-muted/50 border-0 rounded-md px-4 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Enter new email"
                    />
                    <Button
                      onClick={handleChangeEmail}
                      disabled={isChangingEmail || !newEmail.trim()}
                      size="sm"
                    >
                      {isChangingEmail ? "Sending..." : "Send verification"}
                    </Button>
                  </div>
                )}
              </div>

              {/* Password Section */}
              <div className="flex items-center justify-between py-4 border-b">
                <div className="flex-1 max-w-2xl">
                  <h4 className="font-medium mb-1">Password</h4>
                  <p className="text-sm text-muted-foreground">
                    We'll send you an email with a link to reset your password.
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleResetPassword}
                  disabled={isChangingPassword}
                >
                  {isChangingPassword ? "Sending..." : "Reset password"}
                </Button>
              </div>

              {/* 2-Step Verification Section */}
              <div className="py-4 border-b">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex-1 max-w-2xl">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium">2-step verification</h4>
                      {mfaEnabled && (
                        <Badge variant="secondary" className="h-5">
                          <Check className="h-3 w-3 mr-1" />
                          Enabled
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Add an additional layer of security to your account during login.
                    </p>
                  </div>
                  {isLoadingMFA ? (
                    <Button variant="outline" size="sm" disabled>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Loading...
                    </Button>
                  ) : mfaEnabled ? null : (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setMfaSetupOpen(true)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add method
                    </Button>
                  )}
                </div>
                
                {mfaEnabled && mfaFactors.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {mfaFactors.map((factor) => (
                      <div key={factor.id} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                        <div className="flex items-center gap-3">
                          <Shield className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium">Authenticator App</p>
                            <p className="text-xs text-muted-foreground">
                              Active since {new Date(factor.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDisableMFA(factor.id)}
                          disabled={isLoadingMFA}
                        >
                          Disable
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>
          </div>
        );
      case "billing":
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold mb-2">Billing</h2>
              <p className="text-sm text-muted-foreground">
                Manage your subscription and billing information
              </p>
            </div>
            <div className="space-y-6">
              <SubscriptionPlans />
              <BillingForm />
            </div>
          </div>
        );
      case "preferences":
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold mb-2">Preferences</h2>
              <p className="text-sm text-muted-foreground">
                Customize your queueOS experience
              </p>
            </div>
            
            {/* Theme Selection */}
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-medium mb-1">Appearance</h3>
                  <p className="text-sm text-muted-foreground">
                    Customize how queueOS looks on your device
                  </p>
                </div>
                
                <Select
                  value={theme || "system"}
                  onValueChange={(value) => {
                    // Apply theme immediately for instant feedback
                    setTheme(value);
                    
                    // Save to database async without awaiting
                    if (user) {
                      supabase
                        .from('profiles')
                        .update({ theme_preference: value })
                        .eq('user_id', user.id)
                        .then(({ error }) => {
                          if (error) {
                            toast.error("Failed to save theme preference");
                          } else {
                            toast.success(`Theme set to ${value === 'system' ? 'use system setting' : value} mode`);
                          }
                        });
                    }
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="system">Use system setting</SelectItem>
                    <SelectItem value="light">Light</SelectItem>
                    <SelectItem value="dark">Dark</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Sidebar Setting */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium mb-1">Dashboard Sidebar</h3>
                  <p className="text-sm text-muted-foreground">
                    Control whether the sidebar is hidden by default
                  </p>
                </div>
                
                <Switch
                  id="sidebar-hidden"
                  checked={sidebarHidden}
                  onCheckedChange={async (checked) => {
                    setSidebarHidden(checked);
                    
                    if (user) {
                      const { error } = await supabase
                        .from('profiles')
                        .update({ sidebar_hidden_by_default: checked })
                        .eq('user_id', user.id);
                      
                      if (error) {
                        toast.error("Failed to save sidebar preference");
                      } else {
                        toast.success(checked ? "Sidebar will be hidden by default" : "Sidebar will be shown by default");
                      }
                    }
                  }}
                />
              </div>
            </div>
          </div>
        );
      case "notifications":
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold mb-2">Notifications</h2>
              <p className="text-sm text-muted-foreground">
                Manage your notification preferences
              </p>
            </div>
            
            {/* Task Notifications Card */}
            <div className="rounded-lg border p-6 space-y-4">
              <h3 className="font-semibold text-lg">Task Notifications</h3>
              
              <div className="flex items-center justify-between py-2">
                <div className="flex-1">
                  <h4 className="font-medium">Enable Assigned Task Notifications</h4>
                  <p className="text-sm text-muted-foreground">
                    Get notified when a task is assigned to you
                  </p>
                </div>
                <Switch
                  checked={notifyAssignedTasks}
                  onCheckedChange={async (checked) => {
                    setNotifyAssignedTasks(checked);
                    await updateNotificationPreference('notify_assigned_tasks', checked);
                  }}
                />
              </div>
              
              <div className="flex items-center justify-between py-2">
                <div className="flex-1">
                  <h4 className="font-medium">Enable Form Submission Notifications</h4>
                  <p className="text-sm text-muted-foreground">
                    Get notified when someone submits a form to your queue
                  </p>
                </div>
                <Switch
                  checked={notifyFormSubmissions}
                  onCheckedChange={async (checked) => {
                    setNotifyFormSubmissions(checked);
                    await updateNotificationPreference('notify_form_submissions', checked);
                  }}
                />
              </div>
            </div>
            
            {/* Document Notifications Card */}
            <div className="rounded-lg border p-6 space-y-4">
              <h3 className="font-semibold text-lg">Document Notifications</h3>
              
              <div className="flex items-center justify-between py-2">
                <div className="flex-1">
                  <h4 className="font-medium">Enable Mention Notifications</h4>
                  <p className="text-sm text-muted-foreground">
                    Get notified when someone mentions you in a document
                  </p>
                </div>
                <Switch
                  checked={notifyMentions}
                  onCheckedChange={async (checked) => {
                    setNotifyMentions(checked);
                    await updateNotificationPreference('notify_mentions', checked);
                  }}
                />
              </div>
            </div>
          </div>
        );
      case "quinn":
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold mb-2">Quinn AI</h2>
              <p className="text-sm text-muted-foreground">
                AI-powered assistant for queue management
              </p>
            </div>
            
            <div className="rounded-lg border p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg">Enable Quinn AI</h3>
                    <Badge variant="secondary" className="text-xs">Beta</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Let Quinn help you organize and manage your queues with AI
                  </p>
                </div>
                {hasQuinnBetaAccess ? (
                  <Switch
                    checked={quinnBetaEnabled}
                    onCheckedChange={handleToggleQuinnBeta}
                    disabled={isSavingQuinnBeta}
                  />
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <Switch
                          checked={false}
                          disabled={true}
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Quinn is in Private Beta</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </div>

            {/* Show Credit Usage Setting */}
            {hasQuinnBetaAccess && (
              <div className="rounded-lg border p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">Show Credit Usage</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Display token usage (input/output tokens) for each Quinn response
                    </p>
                  </div>
                  <Switch
                    checked={showQuinnCreditUsage}
                    onCheckedChange={handleToggleQuinnCreditUsage}
                    disabled={isSavingQuinnCreditUsage}
                  />
                </div>
              </div>
            )}

            {/* Beta Features Section - Only visible to users with beta access */}
            {hasQuinnBetaAccess && (
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-6 space-y-4">
                <div className="flex items-center gap-2">
                  <Beaker className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-lg">Beta Tester</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  You have access to Quinn AI beta features. Enable the toggle above to start using experimental AI capabilities before they're released to everyone.
                </p>
              </div>
            )}
          </div>
        );
      case "connections":
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold mb-2">Connections</h2>
              <p className="text-sm text-muted-foreground">
                Connect queueOS with other tools and services
              </p>
            </div>
            
            {/* Connections Cards */}
            <div className="space-y-4">
              {/* Google Account Connection */}
              <div className="rounded-lg border p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <svg className="h-5 w-5" viewBox="0 0 24 24">
                        <path
                          fill="currentColor"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="currentColor"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="currentColor"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="currentColor"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium mb-1">Google Account</h3>
                      <p className="text-sm text-muted-foreground">
                        {user?.identities?.some(identity => identity.provider === 'google')
                          ? `Connected as ${user?.email}`
                          : 'Connect your Google account for easier sign-in'}
                      </p>
                    </div>
                  </div>
                  {user?.identities?.some(identity => identity.provider === 'google') ? (
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="h-6">
                        <Check className="h-3 w-3 mr-1" />
                        Connected
                      </Badge>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => {
                              setUnlinkProvider('google');
                              setShowUnlinkConfirm(true);
                            }}
                          >
                            Unlink
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          const redirectTo = `${window.location.origin}/dashboard`;
                          const { error } = await supabase.auth.linkIdentity({
                            provider: 'google',
                            options: {
                              redirectTo
                            }
                          });
                          if (error) throw error;
                          toast.success("Google account linking initiated");
                        } catch (error: any) {
                          console.error('Error linking Google account:', error);
                          toast.error(error.message || "Failed to link Google account");
                        }
                      }}
                    >
                      Connect
                    </Button>
                  )}
                </div>
              </div>

              {/* Discord Account Connection */}
              <div className="rounded-lg border p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium mb-1">Discord Account</h3>
                      <p className="text-sm text-muted-foreground">
                        {user?.identities?.some(identity => identity.provider === 'discord')
                          ? `Connected as ${user?.email}`
                          : 'Connect your Discord account for easier sign-in'}
                      </p>
                    </div>
                  </div>
                  {user?.identities?.some(identity => identity.provider === 'discord') ? (
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="h-6">
                        <Check className="h-3 w-3 mr-1" />
                        Connected
                      </Badge>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => {
                              setUnlinkProvider('discord');
                              setShowUnlinkConfirm(true);
                            }}
                          >
                            Unlink
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          const redirectTo = `${window.location.origin}/dashboard`;
                          const { error } = await supabase.auth.linkIdentity({
                            provider: 'discord',
                            options: {
                              redirectTo
                            }
                          });
                          if (error) throw error;
                          toast.success("Discord account linking initiated");
                        } catch (error: any) {
                          console.error('Error linking Discord account:', error);
                          toast.error(error.message || "Failed to link Discord account");
                        }
                      }}
                    >
                      Connect
                    </Button>
                  )}
                </div>
              </div>

              {/* Apple Account Connection */}
              <div className="rounded-lg border p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium mb-1">Apple Account</h3>
                      <p className="text-sm text-muted-foreground">
                        {user?.identities?.some(identity => identity.provider === 'apple')
                          ? `Connected as ${user?.email}`
                          : 'Connect your Apple account for easier sign-in'}
                      </p>
                    </div>
                  </div>
                  {user?.identities?.some(identity => identity.provider === 'apple') ? (
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="h-6">
                        <Check className="h-3 w-3 mr-1" />
                        Connected
                      </Badge>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => {
                              setUnlinkProvider('apple');
                              setShowUnlinkConfirm(true);
                            }}
                          >
                            Unlink
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          const redirectTo = `${window.location.origin}/dashboard`;
                          const { error } = await supabase.auth.linkIdentity({
                            provider: 'apple',
                            options: {
                              redirectTo
                            }
                          });
                          if (error) throw error;
                          toast.success("Apple account linking initiated");
                        } catch (error: any) {
                          console.error('Error linking Apple account:', error);
                          toast.error(error.message || "Failed to link Apple account");
                        }
                      }}
                    >
                      Connect
                    </Button>
                  )}
                </div>
              </div>

              {/* Spotify Connection */}
              <div className="rounded-lg border p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 rounded-lg bg-[#1DB954]/10 flex items-center justify-center">
                      <svg className="h-5 w-5 text-[#1DB954]" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium mb-1">Spotify</h3>
                      <p className="text-sm text-muted-foreground">
                        {spotifyConnected
                          ? `Connected${spotifyEmail ? ` as ${spotifyEmail}` : ''}`
                          : 'Connect Spotify to control playback from queueOS'}
                      </p>
                    </div>
                  </div>
                  {spotifyConnected ? (
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="h-6">
                        <Check className="h-3 w-3 mr-1" />
                        Connected
                      </Badge>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => setShowSpotifyUnlinkConfirm(true)}
                          >
                            Unlink
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={isLoadingSpotify}
                      onClick={async () => {
                        try {
                          setIsLoadingSpotify(true);
                          const { data: { session } } = await supabase.auth.getSession();
                          if (!session) throw new Error('Not authenticated');

                          const response = await supabase.functions.invoke('spotify-oauth-init', {
                            headers: {
                              Authorization: `Bearer ${session.access_token}`
                            }
                          });

                          if (response.error) throw response.error;
                          if (!response.data?.success) throw new Error(response.data?.error || 'Failed to start OAuth');

                          window.location.href = response.data.auth_url;
                        } catch (error: any) {
                          console.error('Error connecting Spotify:', error);
                          toast.error(error.message || "Failed to connect Spotify");
                        } finally {
                          setIsLoadingSpotify(false);
                        }
                      }}
                    >
                      {isLoadingSpotify ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Connect'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      case "danger":
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold mb-2 text-destructive">Danger Zone</h2>
              <p className="text-sm text-muted-foreground">
                Irreversible and destructive actions
              </p>
            </div>
            <div className="space-y-4">
              <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-6">
                <div className="flex items-start gap-4">
                  <AlertTriangle className="h-5 w-5 text-destructive mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold mb-1">Delete Account</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Permanently delete your account and all associated data. This action cannot be undone.
                    </p>
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => setDeleteAccountOpen(true)}
                    >
                      Delete Account
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // Get current section label and icon for mobile dropdown
  const getCurrentSectionInfo = () => {
    const section = sidebarItems.find(item => item.id === activeSection);
    return section || sidebarItems[0];
  };

  // Mobile content component to avoid duplication
  const MobileSettingsContent = () => (
    <div className="flex flex-col h-full">
      {/* Mobile Header with Close Button */}
      <div className="flex items-center justify-between p-4 border-b">
        <h2 className="text-lg font-semibold">Settings</h2>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onOpenChange(false)}
        >
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Mobile Section Selector */}
      <div className="p-4 border-b bg-muted/30">
        <Select
          value={activeSection}
          onValueChange={(value) => setActiveSection(value as SettingsSection)}
        >
          <SelectTrigger className="w-full">
            <SelectValue>
              <div className="flex items-center gap-2">
                {(() => {
                  const currentSection = getCurrentSectionInfo();
                  const Icon = currentSection.icon;
                  const itemWithBadge = currentSection as typeof currentSection & { badge?: string; customIcon?: string };
                  return (
                    <>
                      {itemWithBadge.customIcon ? (
                        <img src={itemWithBadge.customIcon} alt="" className="h-4 w-4 object-contain" />
                      ) : Icon ? (
                        <Icon className="h-4 w-4" />
                      ) : null}
                      <span>{currentSection.label}</span>
                      {itemWithBadge.badge && (
                        <Badge variant="secondary" className="text-xs">
                          {itemWithBadge.badge}
                        </Badge>
                      )}
                    </>
                  );
                })()}
              </div>
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="bg-background border shadow-lg z-50">
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              const itemWithBadge = item as typeof item & { badge?: string; customIcon?: string };
              return (
                <SelectItem key={item.id} value={item.id}>
                  <div className="flex items-center gap-2">
                    {itemWithBadge.customIcon ? (
                      <img src={itemWithBadge.customIcon} alt="" className="h-4 w-4 object-contain" />
                    ) : Icon ? (
                      <Icon className="h-4 w-4" />
                    ) : null}
                    <span>{item.label}</span>
                    {itemWithBadge.badge && (
                      <Badge variant="secondary" className="text-xs ml-2">
                        {itemWithBadge.badge}
                      </Badge>
                    )}
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {/* Mobile Content Area */}
      <ScrollArea className="flex-1">
        <div className="p-4 pb-8">
          {renderContent()}
        </div>
      </ScrollArea>
    </div>
  );

  // Render mobile drawer or desktop dialog
  if (isMobile) {
    return (
      <>
        <Drawer open={open} onOpenChange={onOpenChange}>
          <DrawerContent className="h-[90vh] max-h-[90vh]">
            <MobileSettingsContent />
          </DrawerContent>
        </Drawer>

        <MFASetupDialog 
          open={mfaSetupOpen}
          onOpenChange={setMfaSetupOpen}
          onSuccess={loadMFAStatus}
        />

        <DeleteAccountDialog
          open={deleteAccountOpen}
          onOpenChange={setDeleteAccountOpen}
          mfaEnabled={mfaEnabled}
        />

        <AlertDialog open={showUnlinkConfirm} onOpenChange={setShowUnlinkConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Unlink {unlinkProvider === 'google' ? 'Google' : 'Discord'} Account</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to unlink your {unlinkProvider === 'google' ? 'Google' : 'Discord'} account? You will no longer be able to sign in with {unlinkProvider === 'google' ? 'Google' : 'Discord'}.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={async () => {
                  try {
                    if (!unlinkProvider) return;
                    
                    const identity = user?.identities?.find(identity => identity.provider === unlinkProvider);
                    if (!identity) {
                      toast.error(`No ${unlinkProvider === 'google' ? 'Google' : 'Discord'} account found to unlink`);
                      return;
                    }
                    
                    const { error } = await supabase.auth.unlinkIdentity(identity);
                    if (error) throw error;
                    
                    await supabase.auth.refreshSession();
                    
                    toast.success(`${unlinkProvider === 'google' ? 'Google' : 'Discord'} account unlinked successfully`);
                    setShowUnlinkConfirm(false);
                    setUnlinkProvider(null);
                  } catch (error: any) {
                    console.error('Error unlinking account:', error);
                    toast.error(error.message || "Failed to unlink account");
                  }
                }}
              >
                Unlink
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl h-[85vh] p-0 gap-0 overflow-hidden flex flex-col">
          <div className="flex h-full">
            {/* Left Sidebar */}
            <div className="w-60 border-r bg-muted/30 flex flex-col">
              {/* User Profile Button at Top */}
              <div className="p-4 border-b">
                <Button
                  variant="ghost"
                  className={cn(
                    "w-full justify-start gap-3 h-auto py-3 px-3",
                    activeSection === "account" && "bg-accent"
                  )}
                  onClick={() => setActiveSection("account")}
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={avatarUrl || undefined} alt={getUserDisplayName()} />
                    <AvatarFallback className="text-xs">
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col items-start text-left overflow-hidden">
                    <span className="text-sm font-medium truncate w-full">
                      {getUserDisplayName()}
                    </span>
                    <span className="text-xs text-muted-foreground truncate w-full">
                      {user?.email}
                    </span>
                  </div>
                </Button>
              </div>

              {/* Navigation Items */}
              <nav className="flex-1 p-2 space-y-1">
                {sidebarItems.slice(1).map((item) => {
                  const Icon = item.icon;
                  const itemWithBadge = item as typeof item & { badge?: string; customIcon?: string };
                  return (
                    <Button
                      key={item.id}
                      variant="ghost"
                      className={cn(
                        "w-full justify-start gap-3 text-sm",
                        activeSection === item.id && "bg-accent"
                      )}
                      onClick={() => setActiveSection(item.id)}
                    >
                      <span className="w-4 h-4 flex items-center justify-center shrink-0">
                        {itemWithBadge.customIcon ? (
                          <img src={itemWithBadge.customIcon} alt="" className="h-5 w-5 object-contain" />
                        ) : Icon ? (
                          <Icon className="h-4 w-4" />
                        ) : null}
                      </span>
                      {item.label}
                      {itemWithBadge.badge && (
                        <Badge variant="secondary" className="ml-auto text-xs">
                          {itemWithBadge.badge}
                        </Badge>
                      )}
                    </Button>
                  );
                })}
              </nav>
            </div>

            {/* Right Content Area */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Scrollable Content */}
              <ScrollArea className="flex-1 h-full">
                <div className="p-8 pt-12 min-h-full">
                  {renderContent()}
                </div>
              </ScrollArea>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <MFASetupDialog 
        open={mfaSetupOpen}
        onOpenChange={setMfaSetupOpen}
        onSuccess={loadMFAStatus}
      />

      <DeleteAccountDialog
        open={deleteAccountOpen}
        onOpenChange={setDeleteAccountOpen}
        mfaEnabled={mfaEnabled}
      />

      <AlertDialog open={showUnlinkConfirm} onOpenChange={setShowUnlinkConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unlink {unlinkProvider === 'google' ? 'Google' : 'Discord'} Account</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unlink your {unlinkProvider === 'google' ? 'Google' : 'Discord'} account? You will no longer be able to sign in with {unlinkProvider === 'google' ? 'Google' : 'Discord'}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  if (!unlinkProvider) return;
                  
                  const identity = user?.identities?.find(identity => identity.provider === unlinkProvider);
                  if (!identity) {
                    toast.error(`No ${unlinkProvider === 'google' ? 'Google' : 'Discord'} account found to unlink`);
                    return;
                  }
                  
                  const { error } = await supabase.auth.unlinkIdentity(identity);
                  if (error) throw error;
                  
                  await supabase.auth.refreshSession();
                  
                  toast.success(`${unlinkProvider === 'google' ? 'Google' : 'Discord'} account unlinked successfully`);
                  setShowUnlinkConfirm(false);
                  setUnlinkProvider(null);
                } catch (error: any) {
                  console.error('Error unlinking account:', error);
                  toast.error(error.message || "Failed to unlink account");
                }
              }}
            >
              Unlink
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showSpotifyUnlinkConfirm} onOpenChange={setShowSpotifyUnlinkConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unlink Spotify</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unlink your Spotify account? You will no longer be able to control Spotify playback from queueOS.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  const { error } = await supabase
                    .from('spotify_integrations')
                    .delete()
                    .eq('user_id', user?.id);
                  
                  if (error) throw error;
                  
                  setSpotifyConnected(false);
                  setSpotifyEmail(null);
                  toast.success('Spotify account unlinked successfully');
                  setShowSpotifyUnlinkConfirm(false);
                } catch (error: any) {
                  console.error('Error unlinking Spotify:', error);
                  toast.error(error.message || "Failed to unlink Spotify");
                }
              }}
            >
              Unlink
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
